<template>
    <div
        class="message"
        :class="[
            `message--${message.role}`,
            { 'message--selectable': selectionMode },
            { 'message--selected': isSelected },
            { 'message--disabled-select': selectionMode && !canSelect },
        ]"
        @click="onRowClick"
    >
        <span
            v-if="selectionMode"
            class="select-mark"
            :class="{ 'select-mark--on': isSelected, 'select-mark--off': canSelect }"
            :aria-hidden="!canSelect"
            :title="canSelect ? 'Выбрать' : 'Это сообщение нельзя выбрать'"
        >
            <ChatIcon v-if="isSelected" name="check" :size="11" color="#FFFFFF" />
        </span>

        <div class="avatar" :class="`avatar--${message.role}`">
            <template v-if="message.role === 'user'">{{ userInitials }}</template>
            <ChatIcon v-else name="car" :size="14" color="#FFFFFF" />
        </div>

        <div class="meta">
            <span class="meta-name">{{ authorName }}</span>
            <span v-if="metaStatusBrief" class="meta-dot">·</span>
            <span v-if="metaStatusBrief" class="meta-status meta-status--brief">{{ metaStatusBrief }}</span>
            <span class="meta-dot">·</span>
            <span class="meta-time">{{ formattedTime }}</span>
        </div>

        <div class="body">
            <template v-if="message.role === 'user'">
                <div class="user-bubble">
                    <div class="user-text">{{ userDisplayText }}</div>
                </div>
                <ChatMessageFilters
                    v-if="message.filterContext"
                    :filter-context="message.filterContext"
                />
            </template>

            <template v-else>
                <div class="answer-shell">
                    <template v-if="showProcess">
                        <div class="answer-node-rail" aria-hidden="true">
                            <span class="answer-node-dot" />
                            <span v-if="showRailLine('process')" class="answer-node-line" />
                        </div>
                        <div class="answer-node-body">
                            <ChatProcessTimeline
                                :steps="message.progressSteps"
                                :streaming="message.status === 'streaming'"
                            />
                        </div>
                    </template>

                    <template v-if="showTimeline">
                        <div class="answer-node-rail" aria-hidden="true">
                            <span class="answer-node-dot" />
                            <span v-if="showRailLine('timeline')" class="answer-node-line" />
                        </div>
                        <div class="answer-node-body">
                            <ChatAnswerTimeline
                                embedded
                                :sources="renderedSources"
                                :streaming="message.status === 'streaming'"
                            />
                        </div>
                    </template>

                    <template v-if="showTyping">
                        <div class="answer-node-rail" aria-hidden="true">
                            <span class="answer-node-dot answer-node-dot--active" />
                        </div>
                        <div class="answer-node-body">
                            <ChatTypingIndicator :label="metaStatus" />
                        </div>
                    </template>

                    <template v-if="showText">
                        <div class="answer-node-rail" aria-hidden="true">
                            <span class="answer-node-dot" />
                            <span v-if="showRailLine('text')" class="answer-node-line" />
                        </div>
                        <div class="answer-node-body">
                            <ChatAnswerText
                                :text="message.text"
                                :content="textContent"
                                :sources="renderedSources"
                            />
                        </div>
                    </template>

                    <template v-for="(card, idx) in message.cards" :key="`card-${idx}`">
                        <div class="answer-node-rail" aria-hidden="true">
                            <span class="answer-node-dot" />
                            <span v-if="showRailLine(`card-${idx}`)" class="answer-node-line" />
                        </div>
                        <div class="answer-node-body">
                            <ChatAnswerCard
                                :card-type="card.cardType"
                                :payload="card.payload"
                                :view-config="card.viewConfig"
                                @update:view-config="onCardViewConfig(idx, $event)"
                            />
                        </div>
                    </template>

                    <template v-for="(list, idx) in message.lists" :key="`list-${idx}`">
                        <div class="answer-node-rail" aria-hidden="true">
                            <span class="answer-node-dot" />
                            <span v-if="showRailLine(`list-${idx}`)" class="answer-node-line" />
                        </div>
                        <div class="answer-node-body">
                            <ChatNumberedList
                                :title="list.title"
                                :items="list.items"
                                :sources="renderedSources"
                            />
                        </div>
                    </template>

                    <template v-for="(callout, idx) in message.callouts" :key="`call-${idx}`">
                        <div class="answer-node-rail" aria-hidden="true">
                            <span class="answer-node-dot" />
                            <span v-if="showRailLine(`callout-${idx}`)" class="answer-node-line" />
                        </div>
                        <div class="answer-node-body">
                            <ChatInsightCallout
                                :tone="callout.tone"
                                :title="callout.title"
                                :content="callout.content"
                                :sources="renderedSources"
                            />
                        </div>
                    </template>

                    <template v-if="message.followups">
                        <div class="answer-node-rail" aria-hidden="true">
                            <span class="answer-node-dot" />
                            <span v-if="showRailLine('followups')" class="answer-node-line" />
                        </div>
                        <div class="answer-node-body">
                            <ChatFollowupActions
                                :title="message.followups.title"
                                :actions="message.followups.actions"
                                @select="$emit('followup', $event)"
                            />
                        </div>
                    </template>

                    <template v-if="showStreamingStatusFooter">
                        <div class="answer-node-rail" aria-hidden="true">
                            <span class="answer-node-dot answer-node-dot--active" />
                        </div>
                        <div class="answer-node-body">
                            <ChatTypingIndicator :label="metaStatus" />
                        </div>
                    </template>

                    <template v-if="message.status === 'stopped'">
                        <div class="answer-node-rail" aria-hidden="true">
                            <span class="answer-node-dot" />
                            <span v-if="showRailLine('stopped')" class="answer-node-line" />
                        </div>
                        <div class="answer-node-body">
                            <div class="stopped-hint">
                                Генерация остановлена. Нажмите «Сгенерировать заново», чтобы продолжить.
                            </div>
                        </div>
                    </template>

                    <template v-if="message.status === 'done' && !reportView && showMessageToolbar">
                        <div class="answer-node-rail answer-node-rail--end" aria-hidden="true">
                            <span class="answer-node-dot answer-node-dot--end" />
                        </div>
                        <div class="answer-node-body answer-node-body--toolbar">
                            <div
                                class="message-toolbar"
                                :class="{ 'message-toolbar--no-versions': !hasMultipleVersions }"
                            >
                                <div v-if="!adminView" class="toolbar-actions">
                                    <button
                                        type="button"
                                        class="pill pill--primary"
                                        title="Редактировать ответ"
                                        @click.stop="$emit('edit', message.id)"
                                    >
                                        <ChatIcon name="settings" :size="15" color="#FFFFFF" />
                                        <span>Редактировать</span>
                                    </button>

                                    <div class="pill-cluster" role="group" aria-label="Действия с ответом">
                                        <button
                                            type="button"
                                            class="pill pill--icon"
                                            :class="{ 'pill--feedback-active': message.feedback === 'up' }"
                                            :title="message.feedback === 'up' ? 'Оценка: полезно' : 'Полезно'"
                                            @click.stop="onFeedback('up')"
                                        >
                                            <ChatIcon name="thumb-up" :size="15" />
                                        </button>
                                        <button
                                            type="button"
                                            class="pill pill--icon"
                                            :class="{ 'pill--feedback-active pill--feedback-down': message.feedback === 'down' }"
                                            :title="message.feedback === 'down' ? 'Оценка: бесполезно' : 'Бесполезно'"
                                            @click.stop="onFeedback('down')"
                                        >
                                            <ChatIcon name="thumb-down" :size="15" />
                                        </button>
                                        <button
                                            type="button"
                                            class="pill pill--icon"
                                            :class="{ 'pill--copied': copyState }"
                                            :title="copyState ? 'Скопировано' : 'Копировать'"
                                            @click.stop="onCopy"
                                        >
                                            <ChatIcon :name="copyState ? 'check' : 'copy'" :size="15" />
                                        </button>
                                        <button
                                            type="button"
                                            class="pill pill--icon"
                                            title="Сгенерировать заново"
                                            @click.stop="$emit('regenerate', message.id)"
                                        >
                                            <ChatIcon name="refresh" :size="15" />
                                        </button>
                                    </div>
                                </div>

                                <div v-if="hasMultipleVersions && !adminView" class="toolbar-versions">
                                    <ChatMessageVersionNav
                                        :active="activeVersionIndex"
                                        :total="versionTotal"
                                        :label="activeVersionLabel"
                                        :can-prev="activeVersionIndex > 0"
                                        :can-next="activeVersionIndex < versionTotal - 1"
                                        @prev="$emit('version-prev', message.id)"
                                        @next="$emit('version-next', message.id)"
                                    />
                                </div>

                                <div
                                    v-if="message.usage && (message.usage.sourcesCount != null || message.usage.durationMs)"
                                    class="toolbar-meta"
                                >
                                    <span v-if="message.usage.sourcesCount != null">{{ usageSourcesLabel }}</span>
                                    <span v-if="message.usage.durationMs">{{ usageDurationLabel }}</span>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'
import ChatAnswerTimeline from './ChatAnswerTimeline.vue'
import ChatProcessTimeline from './ChatProcessTimeline.vue'
import ChatMessageFilters from './ChatMessageFilters.vue'
import ChatAnswerText from './ChatAnswerText.vue'
import ChatAnswerCard from '@/components/chat/cards/ChatAnswerCard.vue'
import ChatNumberedList from './ChatNumberedList.vue'
import ChatInsightCallout from './ChatInsightCallout.vue'
import ChatFollowupActions from './ChatFollowupActions.vue'
import ChatMessageVersionNav from './ChatMessageVersionNav.vue'
import ChatTypingIndicator from './ChatTypingIndicator.vue'
import { stripRegenerationInstruction } from '@/components/chat/state/messageVersions'
const props = defineProps({
    message: { type: Object, required: true },
    userInitials: { type: String, default: 'ГМ' },
    userName: { type: String, default: '' },
    selectionMode: { type: Boolean, default: false },
    isSelected: { type: Boolean, default: false },
    isStreaming: { type: Boolean, default: false },
    activeMessageId: { type: String, default: null },
    /** Публичный отчёт: без редактирования, версий и перегенерации */
    reportView: { type: Boolean, default: false },
    /** Админ-просмотр треда: только переключение вариантов, без действий пользователя */
    adminView: { type: Boolean, default: false },
})

const emit = defineEmits([
    'regenerate',
    'copy',
    'feedback',
    'followup',
    'toggle-select',
    'update-card-view',
    'edit',
    'version-prev',
    'version-next',
])

const userDisplayText = computed(() => stripRegenerationInstruction(props.message.text))

function onCardViewConfig(cardIndex, viewConfig) {
    emit('update-card-view', { messageId: props.message.id, cardIndex, viewConfig })
}

const versionTotal = computed(() =>
    Array.isArray(props.message.versions) ? props.message.versions.length : 0,
)

const hasMultipleVersions = computed(() => versionTotal.value > 1)

const showMessageToolbar = computed(() => {
    if (props.reportView || props.adminView) return false
    return true
})

const activeVersionIndex = computed(() =>
    Number.isFinite(props.message.activeVersionIndex)
        ? props.message.activeVersionIndex
        : 0,
)

const activeVersionLabel = computed(() => {
    const snap = props.message.versions?.[activeVersionIndex.value]
    if (!snap) return ''
    if (activeVersionIndex.value === 0) return 'Исходный ответ'
    return snap.formatLabel || `Вариант ${activeVersionIndex.value + 1}`
})

// Сообщения без serverId (только что отправленные, ещё не сохранены) — нельзя
// положить в share/publish/my-report, потому что мы ссылаемся на id из БД.
const canSelect = computed(() => typeof props.message.serverId === 'number')

function onRowClick(event) {
    if (!props.selectionMode) return
    // Не перехватываем клики по интерактивным элементам внутри (followups, copy).
    const target = event.target
    if (target && target.closest && target.closest('button, a, input, textarea')) return
    if (!canSelect.value) return
    emit('toggle-select', props.message.id)
}

const showText = computed(() => {
    return Boolean(props.message.text || (props.message.textContent && props.message.textContent.length))
})

const renderedSources = computed(() => {
    if (Array.isArray(props.message.sources) && props.message.sources.length) {
        return props.message.sources
    }
    return props.message.source ? [props.message.source] : []
})

const hasMainContent = computed(() => {
    const m = props.message
    return showText.value
        || (m.cards?.length > 0)
        || (m.lists?.length > 0)
        || (m.callouts?.length > 0)
        || Boolean(m.followups)
})

const showTimeline = computed(() => renderedSources.value.length > 0)

// Таймлайн процесса работы агента: план → сбор по источникам → синтез → шаги.
// Показываем для AI-сообщений, у которых накопились шаги (live или из истории).
const showProcess = computed(() =>
    props.message.role === 'ai'
    && Array.isArray(props.message.progressSteps)
    && props.message.progressSteps.length > 0,
)

const showTyping = computed(() =>
    props.message.status === 'streaming'
    && !hasMainContent.value
    && !showTimeline.value
    && !showText.value,
)

const showStreamingStatusFooter = computed(() =>
    props.message.role === 'ai'
    && props.message.status === 'streaming'
    && !showTyping.value,
)

const contentBlockKeys = computed(() => {
    const keys = []
    if (showProcess.value) keys.push('process')
    if (showTimeline.value) keys.push('timeline')
    if (showTyping.value) keys.push('typing')
    if (showText.value) keys.push('text')
    const m = props.message
    ;(m.cards || []).forEach((_, i) => keys.push(`card-${i}`))
    ;(m.lists || []).forEach((_, i) => keys.push(`list-${i}`))
    ;(m.callouts || []).forEach((_, i) => keys.push(`callout-${i}`))
    if (m.followups) keys.push('followups')
    if (showStreamingStatusFooter.value) keys.push('streaming-status')
    if (m.status === 'stopped') keys.push('stopped')
    return keys
})

const lastContentBlockKey = computed(() => {
    const keys = contentBlockKeys.value
    return keys.length ? keys[keys.length - 1] : null
})

function showRailLine(blockKey) {
    const isLast = blockKey === lastContentBlockKey.value
    if (props.message.status === 'done') {
        if (props.reportView) return !isLast
        return true
    }
    return !isLast
}

const textContent = computed(() => {
    if (props.message.status === 'done' && props.message.textContent) {
        return props.message.textContent
    }
    return null
})

const authorName = computed(() => {
    if (props.message.role === 'user') {
        return props.userName || 'Вы'
    }
    return 'AI-аналитик'
})

// Заготовки «имитации жизни», когда бэк молчит дольше 5 секунд.
// Прокручиваются по кругу. Сознательно нейтральные/процессные глаголы —
// чтобы фраза не противоречила тому, что агент реально делает в этот момент.
const STREAMING_FILLER_STATUSES = Object.freeze([
    'анализирую данные',
    'обрабатываю запрос',
    'разбираюсь в деталях',
    'сопоставляю источники',
    'проверяю гипотезу',
    'считаю показатели',
    'ищу зависимости',
    'формирую вывод',
    'уточняю цифры',
    'смотрю динамику',
    'агрегирую результат',
    'проверяю покрытие данных',
    'свожу таблицу',
    'выявляю аномалии',
    'структурирую ответ',
    'нахожу ключевые точки',
    'рассчитываю долю',
    'сверяю периоды',
    'оцениваю значимость',
    'дочитываю контекст',
])

// Сколько мс ждать обновления статуса от бэка, прежде чем самим начать крутить
// заготовки. 5000 мс — компромисс: не мельтешим зря и не оставляем юзера в
// неопределённости.
const STATUS_WATCHDOG_MS = 5000

// Интервал смены фразы-заготовки, когда watchdog уже сработал. Короче, чем
// порог watchdog — иначе пользователь решит, что снова всё подвисло.
const STATUS_FILLER_TICK_MS = 3000

const fillerStatus = ref('')
const lastProgressAt = ref(0)
let fillerIndex = 0
let fillerTimer = null

function clearFillerTimer() {
    if (fillerTimer) {
        clearInterval(fillerTimer)
        fillerTimer = null
    }
}

function ensureFillerTimer() {
    if (fillerTimer) return
    fillerTimer = setInterval(() => {
        if (props.message.role !== 'ai' || props.message.status !== 'streaming') {
            clearFillerTimer()
            return
        }
        const sinceUpdate = Date.now() - (lastProgressAt.value || 0)
        if (sinceUpdate < STATUS_WATCHDOG_MS) return
        fillerIndex = (fillerIndex + 1) % STREAMING_FILLER_STATUSES.length
        fillerStatus.value = STREAMING_FILLER_STATUSES[fillerIndex]
    }, STATUS_FILLER_TICK_MS)
}

// Когда бэк присылает новый progress — сбрасываем watchdog и счётчик заготовок,
// чтобы при следующем простое фраза начала листаться с начала и не вступала в
// диссонанс с настоящим статусом.
watch(
    () => props.message.progress,
    (val) => {
        if (val) {
            lastProgressAt.value = Date.now()
            fillerStatus.value = ''
            fillerIndex = 0
        }
    },
    { immediate: true },
)

// Старт/стоп watchdog по жизненному циклу streaming-ответа.
watch(
    () => [props.message.role, props.message.status],
    ([role, status]) => {
        if (role !== 'ai' || status !== 'streaming') {
            clearFillerTimer()
            fillerStatus.value = ''
            fillerIndex = 0
            return
        }
        if (!lastProgressAt.value) lastProgressAt.value = Date.now()
        ensureFillerTimer()
    },
    { immediate: true },
)

onBeforeUnmount(clearFillerTimer)

const metaStatusBrief = computed(() => {
    if (props.message.role !== 'ai') return ''
    const status = props.message.status
    if (status === 'stopped') return 'остановлено'
    if (status === 'error') return 'ошибка'
    return ''
})

const metaStatus = computed(() => {
    if (props.message.role !== 'ai') return ''
    if (props.message.status !== 'streaming') return ''
    // Приоритет: настоящий progress > заготовка watchdog > дефолт.
    const real = (props.message.progress || '').trim()
    if (real) return real
    if (fillerStatus.value) return fillerStatus.value
    return 'отвечает'
})

const formattedTime = computed(() => {
    const raw = props.message.createdAt
    if (!raw) return ''
    try {
        const d = new Date(raw)
        return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    } catch (error) {
        return ''
    }
})

const usageSourcesLabel = computed(() => {
    const n = props.message.usage?.sourcesCount
    if (n == null) return ''
    const mod10 = n % 10
    const mod100 = n % 100
    if (mod10 === 1 && mod100 !== 11) return `${n} источник`
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${n} источника`
    return `${n} источников`
})

const usageDurationLabel = computed(() => {
    const ms = props.message.usage?.durationMs
    if (!ms) return ''
    return `${(ms / 1000).toFixed(1)} с`
})

// --- Копирование с визуальной обратной связью ---
const copyState = ref(false)
let copyTimer = null

function onCopy() {
    const fallback = props.message.text || ''
    try {
        navigator.clipboard.writeText(fallback)
    } catch (error) {
        // ignore
    }
    // Показываем "Скопировано" на 2 секунды
    copyState.value = true
    if (copyTimer) clearTimeout(copyTimer)
    copyTimer = setTimeout(() => {
        copyState.value = false
        copyTimer = null
    }, 2000)
    emit('copy')
}

// --- Feedback (полезно / бесполезно) ---
function onFeedback(value) {
    emit('feedback', value)
}
</script>

<style scoped>
.message {
    display: grid;
    grid-template-columns: 28px 1fr;
    grid-template-areas:
        "avatar meta"
        "body body";
    column-gap: 12px;
    row-gap: 8px;
    padding: 10px 0;
    transition: background-color 0.15s ease, padding-left 0.15s ease;
    border-radius: 12px;
}

.message--selectable {
    grid-template-columns: 22px 28px 1fr;
    grid-template-areas:
        "select avatar meta"
        "select body body";
    cursor: pointer;
    padding-left: 6px;
}

.message--selectable:hover {
    background: #f7f7f7;
}

.message--selected {
    background: #f0f0f0;
}

.message--disabled-select {
    cursor: not-allowed;
    opacity: 0.6;
}

.select-mark {
    grid-area: select;
    align-self: start;
    margin-top: 6px;
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 1.5px solid #c0c0c0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background-color 0.12s ease, border-color 0.12s ease;
}

.select-mark--off {
    border-color: #c0c0c0;
    background: white;
}

.select-mark--on {
    background: #E30613;
    border-color: #E30613;
}

.avatar {
    grid-area: avatar;
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
}

.meta {
    grid-area: meta;
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #888888;
    line-height: 28px;
    min-width: 0;
    flex-wrap: wrap;
    letter-spacing: 0.02em;
}

.meta-name {
    color: #23282C;
    font-weight: 600;
    font-size: 12px;
}

.message--ai .meta-name {
    text-transform: uppercase;
    font-size: 10px;
    letter-spacing: 0.06em;
    font-weight: 700;
}

.meta-dot {
    color: #c0c0c0;
}

.meta-status--brief {
    color: #888780;
    font-weight: 500;
}

.meta-time {
    font-variant-numeric: tabular-nums;
}

.avatar--user {
    border-radius: 50%;
    background: #e7e7e7;
    color: #23282C;
    border: 1px solid #e0e0e0;
}

.avatar--ai {
    border-radius: 10px;
    background: #23282C;
    color: white;
}

.body {
    grid-area: body;
    width: 100%;
    min-width: 0;
    font-size: 14px;
    line-height: 1.65;
    color: #23282C;
}

.user-bubble {
    display: inline-block;
    max-width: 100%;
    padding: 12px 18px;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 20px 20px 20px 6px;
    box-shadow: 0 2px 10px rgba(21, 26, 34, 0.05);
}

.user-text {
    font-size: 14px;
    line-height: 1.6;
    color: #23282C;
}

.message--user .body {
    font-weight: 500;
}

.stopped-hint {
    margin-top: 14px;
    padding: 12px 16px;
    background: #FFF9E6;
    border: 1px solid #F2DC9C;
    border-radius: 14px;
    font-size: 12px;
    color: #5f5f5f;
}

/* AI-ответ: сетка «точка + линия | блок» на каждый компонент */
.answer-shell {
    display: grid;
    grid-template-columns: 16px 1fr;
    column-gap: 12px;
    row-gap: 0;
    min-width: 0;
}

.answer-node-rail {
    grid-column: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: 10px;
    min-height: 0;
}

/* Точка на уровне строки «Редактировать» (40px), не всего тулбара */
.answer-node-rail--end {
    align-self: start;
    height: 40px;
    padding-top: 0;
    justify-content: center;
}

.answer-node-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #c0c0c0;
    flex-shrink: 0;
    z-index: 1;
}

.answer-node-dot--end {
    width: 9px;
    height: 9px;
    background: #fff;
    border: 2px solid #23282C;
    box-sizing: border-box;
}

.answer-node-dot--active {
    background: #23282C;
    box-shadow: 0 0 0 3px rgba(35, 40, 44, 0.12);
}

.answer-node-line {
    flex: 1;
    width: 1px;
    min-height: 24px;
    margin-top: 4px;
    background: #e0e0e0;
}

.answer-node-body {
    grid-column: 2;
    min-width: 0;
    padding-bottom: 14px;
}

.answer-node-body--toolbar {
    padding-bottom: 0;
    padding-top: 0;
}

.answer-node-body :deep(.card) {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 0;
    margin: 0;
    box-shadow: none;
}

.answer-node-body :deep(.card-head) {
    padding-bottom: 8px;
    margin-bottom: 8px;
    border-bottom: 1px solid #e7e7e7;
}

.answer-node-body :deep(.callout) {
    margin: 0;
    border-radius: 12px;
}

.answer-node-body :deep(.followups) {
    margin: 0;
    padding: 0;
    border: none;
    background: transparent;
}

.message-toolbar {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    width: 100%;
    min-width: 0;
}

.toolbar-versions {
    width: 100%;
    min-width: 0;
}

.toolbar-actions {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    width: 100%;
}

.toolbar-meta {
    font-size: 11px;
    font-weight: 500;
    color: #888888;
    font-variant-numeric: tabular-nums;
    text-align: left;
}

.toolbar-meta span:not(:last-child)::after {
    content: '·';
    margin: 0 8px;
    color: #c0c0c0;
}

/* Pill-кнопки */
.pill {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    border: none;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
}

.pill:active:not(:disabled) {
    transform: scale(0.98);
}

.pill--primary {
    height: 40px;
    min-height: 40px;
    padding: 0 18px;
    border-radius: 999px;
    background: #23282C;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    flex-shrink: 0;
    box-shadow: 0 2px 8px rgba(35, 40, 44, 0.18);
    box-sizing: border-box;
}

.pill--primary:hover {
    background: #3a4048;
    box-shadow: 0 4px 12px rgba(35, 40, 44, 0.22);
}

.pill-cluster {
    display: inline-flex;
    align-items: center;
    gap: 2px;
    height: 40px;
    padding: 3px;
    background: #f0f0f0;
    border: 1px solid #e0e0e0;
    border-radius: 999px;
    box-sizing: border-box;
    flex-shrink: 0;
}

.pill--icon {
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 50%;
    background: transparent;
    color: #5f5f5f;
}

.pill--icon:hover {
    background: #fff;
    color: #23282C;
}

/* Feedback: активная оценка «Полезно» — яркий зелёный на сером фоне */
.pill--feedback-active {
    background: #43A047;
    color: #fff;
}

.pill--feedback-active:hover {
    background: #388E3C;
    color: #fff;
}

/* Feedback: активная оценка «Бесполезно» — яркий красный */
.pill--feedback-active.pill--feedback-down {
    background: #E53935;
    color: #fff;
}

.pill--feedback-active.pill--feedback-down:hover {
    background: #D32F2F;
    color: #fff;
}

/* Копирование: состояние "Скопировано" — яркий зелёный */
.pill--copied {
    background: #43A047;
    color: #fff;
}

.pill--copied:hover {
    background: #388E3C;
    color: #fff;
}

@media (max-width: 639px) {
    .answer-shell {
        grid-template-columns: 12px 1fr;
        column-gap: 10px;
    }

    .message-toolbar {
        gap: 8px;
    }

    .toolbar-actions {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .pill--primary {
        width: 100%;
        flex: none;
    }

    .pill-cluster {
        width: 100%;
        justify-content: center;
    }

    .toolbar-meta {
        text-align: center;
        width: 100%;
        padding-top: 2px;
        line-height: 1.4;
    }

}

@media (min-width: 640px) {
    .message-toolbar {
        display: grid;
        grid-template-columns: 1fr minmax(200px, 280px);
        grid-template-rows: auto auto;
        align-items: center;
        column-gap: 16px;
        row-gap: 6px;
    }

    .toolbar-actions {
        grid-column: 1;
        grid-row: 1;
        flex: none;
        width: auto;
    }

    .toolbar-versions {
        grid-column: 2;
        grid-row: 1;
        width: 100%;
    }

    .toolbar-meta {
        grid-column: 2;
        grid-row: 2;
        text-align: right;
        line-height: 1.3;
        white-space: nowrap;
    }

    .message-toolbar--no-versions {
        grid-template-columns: 1fr auto;
        grid-template-rows: auto;
    }

    .message-toolbar--no-versions .toolbar-meta {
        grid-row: 1;
        line-height: 40px;
    }
}
</style>
